import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {isCustomTagColumnKey, isSchemaTagColumnKey} from 'schemas/tags/ExpandedTableTagUtil';
import Tooltip, {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARK, GRAY_LIGHT} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import LegacyToggleGroup, {LegacyToggleGroupOption} from 'ui-library/LegacyToggleGroup';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import SectionHeader from 'ui-library/menus/SectionHeader';
import {SortDirection} from 'ui-library/scrollable/ScrollableTableConstants';

import {NO_REMAINING_SORTS_MESSAGE} from './SearchConstants';

const DEFAULT_SORT_DIRECTION = SortDirection.ASCENDING_SORT;

export const enum FieldSection {
  STANDARD = 'General',
  SCHEMA_FIELDS = 'Schema Fields',
  CUSTOM_FIELDS = 'Custom Fields',
  CUSTOM_SORTING = 'Custom Sorting',
}

interface SortFormProps {
  selectedSortable: Sortable | null;
  columns: Array<Sortable>;
  onRemove: () => void;
  onChangeSort: (sortable: Sortable) => void;
  // Sorts that are not represented by a column in the search dashboard
  customSortables?: Array<Sortable>;
  removeButtonHidden: boolean;
}

export interface Sortable {
  sortKey: string;
  header: string;
  sortDirection: SortDirection | null;
  columnKey?: string;
}

function mapSortsToSections(
  columns: Array<Sortable>,
  customSortables: Array<Sortable>
): Map<FieldSection, Array<Sortable>> {
  const sectionsToColumns = columns.reduce(
    (map, column) => {
      if (isSchemaTagColumnKey(column.columnKey)) {
        map.get(FieldSection.SCHEMA_FIELDS)!.push(column);
      } else if (isCustomTagColumnKey(column.columnKey)) {
        map.get(FieldSection.CUSTOM_FIELDS)!.push(column);
      } else {
        map.get(FieldSection.STANDARD)!.push(column);
      }
      return map;
    },
    new Map<FieldSection, Array<Sortable>>([
      [FieldSection.CUSTOM_SORTING, []],
      [FieldSection.STANDARD, []],
      [FieldSection.SCHEMA_FIELDS, []],
      [FieldSection.CUSTOM_FIELDS, []],
    ])
  );
  customSortables.forEach((customSortable) => {
    sectionsToColumns.get(FieldSection.CUSTOM_SORTING)!.push(customSortable);
  });
  return sectionsToColumns;
}

interface SortKeyMenuProps {
  columns: Array<Sortable>;
  selectedSortable: Sortable | null;
  onChangeSort: (sortable: Sortable) => void;
  customSortables?: Array<Sortable>;
}

function SortKeyMenu(props: SortKeyMenuProps): JSX.Element {
  const sections = mapSortsToSections(props.columns, props.customSortables ?? []);
  return (
    <Menu
      height={Menu.HEIGHTS.MEDIUM}
      width={Menu.WIDTHS.FULL}
      value={props.selectedSortable}
      scrollable={true}
    >
      {Array.from(sections).map(([header, columns]) => (
        <Section key={header}>
          {header && <SectionHeader className={css(styles.sectionHeader)}>{header}</SectionHeader>}
          {columns.map((column) => (
            <Option
              key={column.sortKey}
              value={column}
              onSelect={() => {
                props.onChangeSort({
                  ...column,
                  sortDirection: column.sortDirection || DEFAULT_SORT_DIRECTION,
                });
              }}
            >
              {column.header}
            </Option>
          ))}
        </Section>
      ))}
    </Menu>
  );
}

function switchOption(direction: SortDirection): LegacyToggleGroupOption<SortDirection> {
  return {
    id: direction,
    buttonProps: {
      icon: (
        <Tooltip title={direction === SortDirection.ASCENDING_SORT ? 'Ascending' : 'Descending'}>
          <BenchlingIcon
            className={css(styles.switchIcon)}
            icon={direction === SortDirection.ASCENDING_SORT ? 'arrow-up' : 'arrow-down'}
          />
        </Tooltip>
      ),
    },
  };
}

interface DirectionSwitchProps {
  selectedSort: Sortable | null;
  onChangeSort: (option: SortDirection) => void;
}

function DirectionSwitch(props: DirectionSwitchProps): JSX.Element {
  return (
    <LegacyToggleGroup<SortDirection>
      leftOption={switchOption(SortDirection.ASCENDING_SORT)}
      rightOption={switchOption(SortDirection.DESCENDING_SORT)}
      selection={props.selectedSort?.sortDirection || DEFAULT_SORT_DIRECTION}
      onChange={props.onChangeSort}
      size={ButtonSize.SMALL}
      disabled={!props.selectedSort}
    />
  );
}

function getTooltipText(areSortFieldsAvailable: boolean, isEllipsized: boolean, buttonText: string): string {
  if (!areSortFieldsAvailable) {
    return NO_REMAINING_SORTS_MESSAGE;
  }
  if (isEllipsized) {
    return buttonText;
  }
  return '';
}

export default function SortForm(props: SortFormProps): JSX.Element {
  const menu = (
    <SortKeyMenu
      columns={props.columns}
      selectedSortable={props.selectedSortable}
      onChangeSort={props.onChangeSort}
      customSortables={props.customSortables}
    />
  );
  const areSortFieldsAvailable = Boolean(props.columns.length || props.customSortables?.length);
  const buttonText = props.selectedSortable?.header || 'Select field';

  const [isEllipsized, setIsEllipsized] = useState(false);
  const buttonTextRef = React.useRef<HTMLSpanElement>(null);
  React.useEffect(() => {
    if (buttonTextRef.current) {
      setIsEllipsized(buttonTextRef.current.scrollWidth > buttonTextRef.current.clientWidth);
    }
  }, [setIsEllipsized, props.selectedSortable]);

  return (
    <div className={css(styles.row)}>
      <div className={css(styles.keyColumn)}>
        <TooltipWrapper tooltipTitle={getTooltipText(areSortFieldsAvailable, isEllipsized, buttonText)}>
          <Dropdown menu={menu}>
            <Button
              buttonStyle={
                props.selectedSortable ? ButtonStyle.TERTIARY_BLUE_POWDER : ButtonStyle.TERTIARY_GRAY
              }
              fullWidth={true}
              isDropdown={true}
              size={ButtonSize.MEDIUM}
              text={buttonText}
              disabled={!areSortFieldsAvailable}
              textClassName={css(styles.buttonText)}
              buttonTextRef={buttonTextRef}
            />
          </Dropdown>
        </TooltipWrapper>
      </div>
      <div className={css(styles.directionColumn)}>
        <DirectionSwitch
          selectedSort={props.selectedSortable}
          onChangeSort={(option: SortDirection) => {
            if (props.selectedSortable) {
              props.onChangeSort({...props.selectedSortable, sortDirection: option});
            }
          }}
        />
      </div>
      {!props.removeButtonHidden ? (
        <BenchlingIcon
          className={css(styles.removeIcon)}
          icon="close"
          onClick={props.onRemove}
          dataTestAttrs={{component: 'SortForm', element: 'remove'}}
        />
      ) : null}
    </div>
  );
}

const styles = StyleSheet.create({
  row: {
    display: 'flex',
  },
  keyColumn: {
    flexGrow: 1,
    flexBasis: 0,
    marginRight: 8,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  directionColumn: {
    width: 120,
    marginRight: 8,
  },
  removeIcon: {
    color: GRAY_DARK,
    cursor: 'pointer',
    fontSize: 14,
    margin: '12px 0px',
  },
  switchIcon: {
    width: '100%',
    outline: 'none',
  },
  sectionHeader: {
    borderTop: `1px solid ${GRAY_LIGHT}`,
  },
  buttonText: {
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});
